import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Box, Snackbar, Alert, Dialog, DialogActions, DialogContent, TextField, IconButton, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { APIURL } from '../../config';
import SaveViewButton from './SaveViewButton';
import DefaultToolbar from './DefaultToolbar';

// This is a custom DataGrid component that allows for the user to save their grid settings
// and have them persist between sessions. It also allows for the user to save their own
// custom views of the grid
// mandatory props:
//      formID: the form id || reportID: the report id
//      user: the user object
//      columns: the columns of the grid
//      rows: the rows of the grid
//      apiRef: the apiRef of the grid

// Besides the mandatory props, you can use this component just like you would use the DataGridPro component

// If you want to use a Custom Toolbar you can pass it as a function that returns the toolbar along
// with the SaveViewButton component. The function should take in params and spread them into the 
// SaveViewButton component like in the DefaultToolbar function below

// optional props:
//      onLoad: a function or array of functions that will be called when the grid loads. The function(s) will be passed the settings object
//      onSave: a function or array of functions that will be called when the user saves the grid settings. The function(s) will be passed the settings object

const ControlledFormDataGrid = (props) => {
    const [isLoadingSettings, setIsLoadingSettings] = useState(true);
    const [initialSettings, setInitialSettings] = useState(null);
    const [orderedColumns, setOrderedColumns] = useState(props.columns ? props.columns.reduce((acc, col) => ([...acc, col.field]), []) : []);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
    const [density, setDensity] = useState("standard");
    const [sortModel, setSortModel] = useState([]);
    const [rows, setRows] = useState(props.rows ? props.rows : []);
    const [openModal, setOpenModal] = useState(false);
    const [reportName, setReportName] = useState('');
    const [reportHeader, setReportHeader] = useState('');
    const [reportSubheader1, setReportSubheader1] = useState('');
    const [reportSubheader2, setReportSubheader2] = useState('');
    const [snackbar, setSnackbar] = useState(null);

    const handleCloseSnackbar = () => setSnackbar(null);

    useEffect(() => {
        async function loadSettings() {
            if (props.reportID) {
                let getGridSettings = await axios.get(`${APIURL}/forms/form_report`, { params: { report_id: props.reportID, report_company_id: props.user.comp_id } });
                if (getGridSettings.data.length > 0) {
                    let settings = JSON.parse(getGridSettings.data[0].report_definition);

                    if (settings.columns?.orderedFields) {
                        const extraColumns = props.columns
                            .map((col) => col.field)
                            .filter((field) => !settings.columns.orderedFields.includes(field));
                        settings.columns.orderedFields = [
                            settings.columns.orderedFields[0],
                            ...extraColumns,
                            ...settings.columns.orderedFields.slice(1),
                        ];
                    }

                    setInitialSettings({ ...settings, ...props.initialState });
                    setColumnVisibilityModel({ ...props.columnVisibilityModel, ...settings.columnVisibilityModel });
                    setDensity(settings.density ? settings.density : "standard");
                    setSortModel(settings.sortModel ? settings.sortModel : []);
                    setOrderedColumns(settings.columns ? settings.columns.orderedFields : []);
                    if (props.onLoad) {
                        Array.isArray(props.onLoad) ? props.onLoad.forEach((func) => func(settings)) : props.onLoad(settings);
                    }
                }
            }
        }
        loadSettings().then(() => {
            setIsLoadingSettings(false);
        }).catch((error) => {
            console.error('Error fetching grid settings:', error);
            setIsLoadingSettings(false);
        });
    }, []);

    useEffect(() => {
        if (initialSettings !== null) {
            setIsLoadingSettings(false);
        }
    }, [initialSettings]);

    useEffect(() => {
        if (props.rows) {
            setRows(props.rows);
        }
    }, [props.rows]);

    const handleColumnOrderChange = useCallback((params) => {
        setOrderedColumns((prevOrderedColumns) => {
            if (!prevOrderedColumns || !Array.isArray(prevOrderedColumns)) {
                return [];
            }
            const newOrderedColumns = [...prevOrderedColumns];
            const oldIndex = params.oldIndex - props.checkboxSelection ? 1 : 0;
            const targetIndex = params.targetIndex - props.checkboxSelection ? 1 : 0;
            const oldColumn = prevOrderedColumns[oldIndex];
            newOrderedColumns.splice(oldIndex, 1);
            newOrderedColumns.splice(targetIndex, 0, oldColumn);
            return newOrderedColumns;
        });
    }, [props.checkboxSelection]);

    const handleColumnVisibilityModelChange = (newModel) => {
        setColumnVisibilityModel(newModel);
    }

    const columns = useMemo(() => {
        let allColumns = props.columns ? props.columns.reduce((acc, col) => ({ ...acc, [col.field]: col }), {}) : {};

        if (initialSettings?.columns?.dimensions) {
            Object.keys(initialSettings.columns.dimensions).forEach((field) => {
                try {
                    if (initialSettings.columns.dimensions.hasOwnProperty(field) && allColumns.hasOwnProperty(field))
                        allColumns[field].width = initialSettings.columns.dimensions[field].width;
                } catch (e) {
                    console.log('ERROR', e);
                }
            });
        }

        let newColumns = (orderedColumns || []).reduce((acc, field) => {
            return [...acc, allColumns[field]];
        }, []);

        const extraColumns = Object.keys(allColumns)
            .filter((field) => !(orderedColumns || []).includes(field))
            .map((field) => allColumns[field]);

        newColumns = [
            newColumns[0],
            ...extraColumns,
            ...newColumns.slice(1),
        ];

        newColumns = newColumns.filter((col) => col !== undefined && col !== null);
        return newColumns;
    }, [rows]);

    const debounce = (func, wait) => {
        let timeout;
        return (...args) => {
            const context = this;
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(context, args), wait);
        };
    };

    const handleSaveSettings = async () => {
        let settings = props.apiRef.current.exportState();
        settings.columnVisibilityModel = columnVisibilityModel;
        settings.density = density;
        settings.sortModel = sortModel;

        const serializedSettings = JSON.stringify(settings, (key, value) =>
            value instanceof Set ? [...value] : value
        );

        if (props.onSave) {
            Array.isArray(props.onSave) ? props.onSave.forEach((func) => func(settings)) : props.onSave(settings);
        }

        let saveGridSettings = props.formID
            ? await axios.post(`${APIURL}/forms/form_report`, { report_name: reportName, report_header: reportHeader, report_subheader1: reportSubheader1, report_subheader2: reportSubheader2, report_owner: props.user.emp_id, report_form_id: props.formID, report_definition: serializedSettings, report_company_id: props.user.comp_id })
                .then(async (response) => {
                    setSnackbar({
                        children: "New report added.",
                        severity: "success",
                    });
                })
                .catch((err) => {
                    console.log(err);
                    setSnackbar({
                        children: err.message,
                        severity: "error"
                    });
                })
            : await axios.put(`${APIURL}/forms/form_report`, { report_id: props.reportID, report_definition: serializedSettings, report_company_id: props.user.comp_id })
                .then(async (response) => {
                    setSnackbar({
                        children: "Report saved.",
                        severity: "success",
                    });
                })
                .catch((err) => {
                    console.log(err);
                    setSnackbar({
                        children: err.message,
                        severity: "error"
                    });
                });

        setOpenModal(false);
    }

    const handleOnClick = async () => {
        if (props.formID) {
            setOpenModal(true);
        } else {
            handleSaveSettings();
        }
    }

    return (<>
        <Box sx={{ width: '100%', height: 'calc(100vh - 210px)' }}>
            <DataGridPro
                {...props}
                sx={{
                    backgroundColor: '#F9F9F9',
                    '& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell, & .MuiDataGrid-filler--pinnedLeft': {
                        backgroundColor: '#F9F9F9',
                    },
                    ...props.sx
                }}
                rows={rows}
                columns={columns}
                density={density}
                onDensityChange={(newDensity) => setDensity(newDensity)}
                initialState={initialSettings}
                slots={{
                    color: '#3fadf6',
                    ...props.components,
                    ...props.slots,
                    toolbar: DefaultToolbar,
                }}
                slotProps={{
                    loadingOverlay: props.slotProps?.loadingOverlay,
                    toolbar: {
                        reportHeader: props.reportHeader,
                        reportSubheader1: props.reportSubheader1,
                        reportSubheader2: props.reportSubheader2,
                        children: (
                            <>
                                {(Array.isArray(props.toolbarButtons) ? props.toolbarButtons : []).map((Btn, idx) => (
                                    <Btn key={idx} />
                                ))}
                                <SaveViewButton onClick={debounce(handleOnClick, 500)} buttonName={props.formID ? 'CREATE NEW REPORT' : 'SAVE REPORT'} />
                            </>
                        ),
                    },
                }}
                onColumnOrderChange={handleColumnOrderChange}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
                sortModel={sortModel}
                onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
                showToolbar={props.showToolbar !== undefined ? props.showToolbar : true}
            />
            <Dialog fullWidth={true} maxWidth={'sm'} open={openModal} onClose={() => setOpenModal(false)}>
                <DialogActions>
                    <IconButton
                        aria-label="close"
                        color="inherit"
                        sx={{ p: 0.25 }}
                        onClick={() => setOpenModal(false)}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogActions>
                <DialogContent sx={{ paddingTop: '0px' }}>
                    <h4>Add Report</h4>
                    <TextField
                        value={reportName}
                        onChange={(event) => {
                            setReportName(event.target.value)
                        }}
                        style={{ margin: '5px', marginBottom: '20px', width: '100%' }}
                        inputProps={{ maxLength: 50 }}
                        label="Report Name *"
                        id="rName"
                        error={!reportName}
                    />
                    <TextField
                        value={reportHeader}
                        onChange={(event) => {
                            setReportHeader(event.target.value)
                        }}
                        style={{ margin: '5px', marginBottom: '20px', width: '100%' }}
                        inputProps={{ maxLength: 50 }}
                        label="Report Header *"
                        id="rHeader"
                        error={!reportHeader}
                    />
                    <TextField
                        value={reportSubheader1}
                        onChange={(event) => {
                            setReportSubheader1(event.target.value)
                        }}
                        style={{ margin: '5px', marginBottom: '20px', width: '100%' }}
                        inputProps={{ maxLength: 50 }}
                        label="Report Subheader 1"
                        id="rSubHeader1"
                    />
                    <TextField
                        value={reportSubheader2}
                        onChange={(event) => {
                            setReportSubheader2(event.target.value)
                        }}
                        style={{ margin: '5px', marginBottom: '20px', width: '100%' }}
                        inputProps={{ maxLength: 50 }}
                        label="Report Subheader 2"
                        id="rSubHeader2"
                    />
                    <Button
                        variant='contained'
                        style={{
                            width: '100%',
                            padding: '5px',
                            margin: '5px',
                        }}
                        onClick={() => handleSaveSettings()}
                    >
                        Add Report
                    </Button>
                </DialogContent>
            </Dialog>
            {!!snackbar && (
                <Snackbar
                    open
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                    onClose={handleCloseSnackbar}
                    autoHideDuration={6000}
                >
                    <Alert {...snackbar} onClose={handleCloseSnackbar} />
                </Snackbar>
            )}
        </Box>
    </>
    );
}

export default ControlledFormDataGrid;